/**
 * 地理计算工具类
 * 提供各种地理距离计算和坐标转换功能
 */

/**
 * 计算两点间的距离（使用Haversine公式）
 * @param {Array} coord1 - 第一个点的坐标 [经度, 纬度]
 * @param {Array} coord2 - 第二个点的坐标 [经度, 纬度]
 * @returns {number} 距离（米）
 */
export function calculateDistance (coord1, coord2) {
  const R = 6371000; // 地球半径（米）
  const lat1 = coord1[1] * Math.PI / 180;
  const lat2 = coord2[1] * Math.PI / 180;
  const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180;
  const deltaLon = (coord2[0] - coord1[0]) * Math.PI / 180;

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // 返回距离（米）
}

/**
 * 计算多点间的总距离
 * @param {Array} coordinates - 坐标点数组 [[经度, 纬度], ...]
 * @returns {number} 总距离（米）
 */
export function calculateTotalDistance (coordinates) {
  if (coordinates.length < 2) {
    return 0;
  }

  let totalDistance = 0;
  for (let i = 0; i < coordinates.length - 1; i++) {
    totalDistance += calculateDistance(coordinates[i], coordinates[i + 1]);
  }
  return totalDistance;
}

/**
 * 格式化距离显示
 * @param {number} distance - 距离（米）
 * @returns {string} 格式化后的距离字符串
 */
export function formatDistance (distance) {
  if (distance < 1000) {
    return `${distance.toFixed(2)} 米`;
  } else {
    return `${(distance / 1000).toFixed(2)} 公里`;
  }
}

/**
 * 计算两点间的方位角
 * @param {Array} coord1 - 起始点坐标 [经度, 纬度]
 * @param {Array} coord2 - 终点坐标 [经度, 纬度]
 * @returns {number} 方位角（度）
 */
export function calculateBearing (coord1, coord2) {
  const lat1 = coord1[1] * Math.PI / 180;
  const lat2 = coord2[1] * Math.PI / 180;
  const deltaLon = (coord2[0] - coord1[0]) * Math.PI / 180;

  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);

  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360; // 转换为0-360度
}

/**
 * 计算多边形面积（使用球面几何）
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {number} 面积（平方米）
 */
export function calculatePolygonArea (coordinates) {
  if (coordinates.length < 3) {
    return 0;
  }

  const R = 6371000; // 地球半径（米）
  let area = 0;

  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;
    const lat1 = coordinates[i][1] * Math.PI / 180;
    const lat2 = coordinates[j][1] * Math.PI / 180;
    const deltaLon = (coordinates[j][0] - coordinates[i][0]) * Math.PI / 180;

    area += deltaLon * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  area = Math.abs(area * R * R / 2);
  return area;
}

/**
 * 计算点到线段的距离
 * @param {Array} point - 点坐标 [经度, 纬度]
 * @param {Array} lineStart - 线段起点坐标 [经度, 纬度]
 * @param {Array} lineEnd - 线段终点坐标 [经度, 纬度]
 * @returns {number} 距离（米）
 */
export function calculatePointToLineDistance (point, lineStart, lineEnd) {
  // 将经纬度转换为平面坐标进行计算
  const x0 = point[0];
  const y0 = point[1];
  const x1 = lineStart[0];
  const y1 = lineStart[1];
  const x2 = lineEnd[0];
  const y2 = lineEnd[1];

  // 计算点到线段的距离
  const A = x0 - x1;
  const B = y0 - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) {
    // 线段退化为点
    return calculateDistance(point, lineStart);
  }

  const param = dot / lenSq;

  let xx, yy;
  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  return calculateDistance(point, [xx, yy]);
}

/**
 * 判断点是否在多边形内部
 * @param {Array} point - 点坐标 [经度, 纬度]
 * @param {Array} polygon - 多边形顶点坐标数组
 * @returns {boolean} 是否在多边形内部
 */
export function isPointInPolygon (point, polygon) {
  const x = point[0];
  const y = point[1];
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * 计算两个多边形的交集面积
 * @param {Array} polygon1 - 第一个多边形顶点坐标数组
 * @param {Array} polygon2 - 第二个多边形顶点坐标数组
 * @returns {number} 交集面积（平方米）
 */
export function calculatePolygonIntersection (polygon1, polygon2) {
  // 这里需要实现多边形交集算法
  // 由于算法复杂，这里提供一个简化版本
  // 实际项目中可能需要使用专门的几何计算库

  // 简化实现：如果两个多边形有重叠，返回较小的面积
  const area1 = calculatePolygonArea(polygon1);
  const area2 = calculatePolygonArea(polygon2);

  // 这里应该实现真正的交集计算
  // 暂时返回0，实际使用时需要实现完整的交集算法
  return 0;
}

/**
 * 坐标转换：WGS84转GCJ02
 * @param {Array} coord - WGS84坐标 [经度, 纬度]
 * @returns {Array} GCJ02坐标 [经度, 纬度]
 */
export function wgs84ToGcj02 (coord) {
  const [lng, lat] = coord;
  const a = 6378245.0;
  const ee = 0.00669342162296594323;

  if (isOutOfChina(lng, lat)) {
    return [lng, lat];
  }

  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);

  const radLat = lat / 180.0 * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - ee * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * Math.PI);
  dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * Math.PI);

  return [lng + dLng, lat + dLat];
}

/**
 * 坐标转换：GCJ02转WGS84
 * @param {Array} coord - GCJ02坐标 [经度, 纬度]
 * @returns {Array} WGS84坐标 [经度, 纬度]
 */
export function gcj02ToWgs84 (coord) {
  const [lng, lat] = coord;
  const a = 6378245.0;
  const ee = 0.00669342162296594323;

  if (isOutOfChina(lng, lat)) {
    return [lng, lat];
  }

  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);

  const radLat = lat / 180.0 * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - ee * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * Math.PI);
  dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * Math.PI);

  return [lng - dLng, lat - dLat];
}

// 辅助函数
function isOutOfChina (lng, lat) {
  return (lng < 72.004 || lng > 137.8347) || (lat < 0.8293 || lat > 55.8271);
}

function transformLat (lng, lat) {
  let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
  ret += (20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(lat * Math.PI) + 40.0 * Math.sin(lat / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (160.0 * Math.sin(lat / 12.0 * Math.PI) + 320 * Math.sin(lat * Math.PI / 30.0)) * 2.0 / 3.0;
  return ret;
}

function transformLng (lng, lat) {
  let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
  ret += (20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(lng * Math.PI) + 40.0 * Math.sin(lng / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (150.0 * Math.sin(lng / 12.0 * Math.PI) + 300.0 * Math.sin(lng / 30.0 * Math.PI)) * 2.0 / 3.0;
  return ret;
}

/**
 * 格式化面积显示
 * @param {number} area - 面积（平方米）
 * @returns {string} 格式化后的面积字符串
 */
export function formatArea (area) {
  if (area < 10000) {
    return `${area.toFixed(2)} 平方米`;
  } else if (area < 1000000) {
    return `${(area / 10000).toFixed(2)} 公顷`;
  } else {
    return `${(area / 1000000).toFixed(2)} 平方公里`;
  }
}

/**
 * 计算多边形的周长
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {number} 周长（米）
 */
export function calculatePolygonPerimeter (coordinates) {
  if (coordinates.length < 3) {
    return 0;
  }

  let perimeter = 0;
  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;
    perimeter += calculateDistance(coordinates[i], coordinates[j]);
  }

  return perimeter;
}

/**
 * 计算多边形的中心点
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {Array} 中心点坐标 [经度, 纬度]
 */
export function calculatePolygonCenter (coordinates) {
  if (coordinates.length === 0) {
    return [0, 0];
  }

  let sumLng = 0;
  let sumLat = 0;

  for (const coord of coordinates) {
    sumLng += coord[0];
    sumLat += coord[1];
  }

  return [sumLng / coordinates.length, sumLat / coordinates.length];
}

/**
 * 计算多边形的边界框
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {Object} 边界框 {minLng, maxLng, minLat, maxLat}
 */
export function calculatePolygonBounds (coordinates) {
  if (coordinates.length === 0) {
    return { minLng: 0, maxLng: 0, minLat: 0, maxLat: 0 };
  }

  let minLng = coordinates[0][0];
  let maxLng = coordinates[0][0];
  let minLat = coordinates[0][1];
  let maxLat = coordinates[0][1];

  for (const coord of coordinates) {
    minLng = Math.min(minLng, coord[0]);
    maxLng = Math.max(maxLng, coord[0]);
    minLat = Math.min(minLat, coord[1]);
    maxLat = Math.max(maxLat, coord[1]);
  }

  return { minLng, maxLng, minLat, maxLat };
}

/**
 * 判断多边形是否为凸多边形
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {boolean} 是否为凸多边形
 */
export function isConvexPolygon (coordinates) {
  if (coordinates.length < 3) {
    return false;
  }

  let sign = 0;
  const n = coordinates.length;

  for (let i = 0; i < n; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % n];
    const p3 = coordinates[(i + 2) % n];

    const cross = (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p2[1] - p1[1]) * (p3[0] - p1[0]);

    if (cross !== 0) {
      if (sign === 0) {
        sign = cross > 0 ? 1 : -1;
      } else if ((cross > 0 ? 1 : -1) !== sign) {
        return false;
      }
    }
  }

  return true;
}

/**
 * 计算多边形的方向（顺时针或逆时针）
 * @param {Array} coordinates - 多边形顶点坐标数组
 * @returns {string} 'clockwise' 或 'counterclockwise'
 */
export function getPolygonOrientation (coordinates) {
  if (coordinates.length < 3) {
    return 'counterclockwise';
  }

  let sum = 0;
  const n = coordinates.length;

  for (let i = 0; i < n; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % n];
    sum += (p2[0] - p1[0]) * (p2[1] + p1[1]);
  }

  return sum > 0 ? 'clockwise' : 'counterclockwise';
}
